// Pure helpers for the account-owner Credits page. No React, no API, so every
// rule the page applies to the GET payload can be unit tested on its own.
// Contract: credits_owner_page_frontend_handoff.md (lucihub-rails, Api::V3::CreditsController).

// The API accepts exactly these; anything else is a 422 "Unknown product".
export const PRODUCTS = [
  { key: 'queenbee', label: 'Lucihub Video' },
  { key: 'smarthub', label: 'SmartHub' },
  { key: 'storybuilder', label: 'Story Builder' },
];

export const MODES = { SHARED: 'shared_pool', PER_USER: 'per_user' };

// QueenBee seats are informational (every active user holds one), and team
// carves exist for QueenBee only in phase 1.
export const READ_ONLY_SEAT_PRODUCTS = ['queenbee'];
export const TEAM_CARVE_PRODUCTS = ['queenbee'];

export const DASH = '—';

// A non-negative whole number, or null. "3" is fine; "3.5", "-1" and "" are not.
export const parseCap = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return null;
  return parseInt(text, 10);
};

// Members across every team, de-duplicated by id (ids may arrive as numbers or strings).
export const uniqueMembers = (teams) => {
  const seen = new Set();
  const out = [];
  (teams || []).forEach((team) => {
    (team?.members || []).forEach((member) => {
      const id = String(member?.id);
      if (member && !seen.has(id)) {
        seen.add(id);
        out.push(member);
      }
    });
  });
  return out;
};

// Every active org user. QueenBee holders are exactly that set; fall back to
// team members when the seats block is absent (unmanaged or no-deal payloads).
export const orgMembers = (data) => {
  const holders = data?.seats?.queenbee?.holders;
  if (Array.isArray(holders) && holders.length) return holders;
  return uniqueMembers(data?.teams);
};

// Members who do not already hold a seat for the product.
export const seatCandidates = (members, holders) => {
  const held = new Set((holders || []).map((h) => String(h.id)));
  return (members || []).filter((m) => !held.has(String(m.id)));
};

// Heading, grant gate and caption for a seats panel. QueenBee is informational
// (the API reports limit/used 0 there), so it shows the member count instead.
// limit 0 means the contract includes no seats; null means no limit.
export const seatSummary = (seats, readOnly) => {
  const holders = seats?.holders || [];
  if (readOnly) {
    const n = holders.length;
    return { heading: `Seats: ${n} ${n === 1 ? 'member' : 'members'}`, full: true, caption: null };
  }
  const limit = seats?.limit;
  const used = seats?.used ?? holders.length;
  if (limit === null || limit === undefined) {
    return { heading: `Seats: ${used} used · No limit`, full: false, caption: null };
  }
  if (limit === 0) {
    return {
      heading: `Seats: ${used} used · no seats included`,
      full: true,
      caption: 'Your contract includes no seats for this product. Contact Lucihub to add some.',
    };
  }
  const full = used >= limit;
  return {
    heading: `Seats: ${used} used · limit ${limit}`,
    full,
    caption: full ? 'All seats are in use; revoke one to grant another.' : null,
  };
};

// Products that get a card: present in allocation or seats, in a fixed order.
export const productsInPayload = (data) =>
  PRODUCTS.filter(({ key }) => !!data?.allocation?.[key] || !!data?.seats?.[key]);

export const productLabel = (key) => PRODUCTS.find((p) => p.key === key)?.label || key;

// "Ben Collaborator" → "BC"; falls back to the email's first letter.
export const initials = (name, email) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length) return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
  return email ? String(email)[0].toUpperCase() : '?';
};

export const display = (value) => (value === null || value === undefined ? DASH : value);

// 0–100 for a LinearProgress, or null when there is nothing to measure against.
export const percentUsed = (used, total) => {
  if (!total || used === null || used === undefined) return null;
  return Math.max(0, Math.min(100, (Number(used) / Number(total)) * 100));
};

// Credits used by a team or user. QueenBee's own spend comes from `usage`; other
// products keep `used` on the stored carve/allowance (not always present).
export const usedFor = (data, product, kind, id) => {
  const key = String(id);
  if (product === 'queenbee') {
    const bucket = kind === 'team' ? data?.usage?.by_team : data?.usage?.by_user;
    return bucket?.[key] ?? 0;
  }
  const allocation = data?.allocation?.[product];
  const rows = kind === 'team' ? allocation?.teamCarves : allocation?.userAllowances;
  return rows?.[key]?.used ?? 0;
};

// The stored cap for a team carve or user allowance, or null when none is set.
export const storedCap = (data, product, kind, id) => {
  const allocation = data?.allocation?.[product];
  const rows = kind === 'team' ? allocation?.teamCarves : allocation?.userAllowances;
  const cap = rows?.[String(id)]?.cap;
  return cap === undefined ? null : cap;
};

// "YYYY-MM-DD" → "Dec 31, 2026" without a timezone shift.
export const formatDay = (isoDay) => {
  if (!isoDay) return DASH;
  const [y, m, d] = String(isoDay).split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return String(isoDay);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

export const formatDateTime = (iso) => {
  if (!iso) return DASH;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? String(iso) : date.toLocaleString();
};

// Decides what the allocation panel's Save does with the typed values.
// Returns { canSave, error, body }: error is a local message shown instead of
// calling the API; body is what to send. per_user always needs a default, on
// every save, so the API never sees a per_user save without one.
export const allocationSaveState = ({ storedMode, storedDefault, mode, defaultText }) => {
  const typed = String(defaultText ?? '').trim();
  const parsed = parseCap(typed);
  const modeChanged = mode !== storedMode;
  const defaultChanged = typed !== '' && parsed !== null && parsed !== (storedDefault ?? null);

  if (typed !== '' && parsed === null) {
    return { canSave: false, error: 'Enter a whole-number default allowance', body: null };
  }
  if (!modeChanged && !defaultChanged) return { canSave: false, error: null, body: null };
  if (mode === MODES.PER_USER && parsed === null) {
    return { canSave: true, error: 'Enter a whole-number default allowance for per-user mode', body: null };
  }
  const body = { mode };
  if (parsed !== null) body.defaultUserAllowance = parsed;
  return { canSave: true, error: null, body };
};

// Which of the five page states to render.
export const pageState = ({ isOwner, isLoading, isError, data }) => {
  if (!isOwner) return 'redirect';
  if (isLoading && !data) return 'loading';
  if (isError) return 'error';
  if (!data) return 'loading';
  if (!data.deal) return 'no_deal';
  if (!data.managed) return 'unmanaged';
  return 'managed';
};

// Pending-key builders, so the page and the hook agree on the spelling.
export const pendingKey = {
  alloc: (product) => `${product}:alloc`,
  team: (product, teamId) => `${product}:team:${teamId}`,
  user: (product, userId) => `${product}:user:${userId}`,
  grant: (product) => `${product}:seat`,
  revoke: (product, userId) => `${product}:seat:${userId}`,
};
